/**
 * Permission tiers and job titles.
 *
 * Two separate ideas, deliberately not merged:
 *
 *   Role      — the permission tier. Three values. The ONLY thing authorised
 *               against, so the rules stay small enough to reason about.
 *   Job title — what the clinic calls the person. Seven values, from
 *               design/Clinic Stock v2.dc.html:5931. Descriptive only.
 *
 * Ported from design/PROTOTYPE-SPEC.md §"Attribution & permissions": billing and
 * plan changes are owner; voids, stock-count apply and team management are manager
 * and up; logging usage and sales is everyone.
 */
export const ROLES = ['owner', 'manager', 'staff'];
export const ROLE_RANK = { staff: 0, manager: 1, owner: 2 };
export const ROLE_LABEL = {
    owner: 'Owner',
    manager: 'Manager',
    staff: 'Staff',
};
/** design/Clinic Stock v2.dc.html:5931 — the prototype's role dropdown, verbatim. */
export const JOB_TITLES = [
    'owner',
    'manager',
    'injector',
    'front',
    'operations',
    'admin',
    'other',
];
export const JOB_TITLE_LABEL = {
    owner: 'Owner',
    manager: 'Manager',
    injector: 'Injector / practitioner',
    front: 'Front of house',
    operations: 'Operations',
    admin: 'Admin',
    other: 'Other',
};
/**
 * The permission tier a job title implies by default.
 *
 * Note `admin` maps to `staff`, not `manager`: in a clinic "admin" means
 * administrative staff — reception, paperwork — not a system administrator. Reading
 * it the other way round would quietly hand out team-management rights.
 *
 * This is only the default offered in the UI. The tier is stored separately and can
 * be set independently, because a trusted injector might well be a manager.
 */
export const DEFAULT_ROLE_FOR_TITLE = {
    owner: 'owner',
    manager: 'manager',
    injector: 'staff',
    front: 'staff',
    operations: 'staff',
    admin: 'staff',
    other: 'staff',
};
/** Actions gated by tier. Anything absent is available to everyone. */
export const PERMISSIONS = {
    /** Plan changes, cancellation, payment methods. */
    manageBilling: ROLE_RANK.owner,
    /** Invite, change roles, deactivate. */
    manageTeam: ROLE_RANK.manager,
    /** Reversing a movement. */
    voidMovement: ROLE_RANK.manager,
    /** Committing a stock count's adjustments. */
    applyStockCount: ROLE_RANK.manager,
    /** Renaming the clinic, VAT rate, notification settings. */
    manageSettings: ROLE_RANK.manager,
    /**
     * Creating, editing and archiving products, classifications and locations.
     *
     * The API has enforced this since Manage stock's endpoints were written
     * (`refuseUnlessManager` in modules/products.ts and modules/taxonomy.ts), but
     * there was no entry here — so the SPA had to hardcode the same rule to know
     * whether to render an editable row. Two copies of a permission is how they
     * drift, and the drift is silent in the safe direction: a UI that offers an
     * edit the API will refuse looks like a broken app, not like a policy.
     *
     * See docs/DECISIONS.md §2 for why this is manager rather than staff — it is
     * the tighter reading of a spec that does not say, chosen because the page
     * carries unit cost and retail price.
     */
    manageProducts: ROLE_RANK.manager,
    /**
     * Reading the audit log.
     *
     * Manager and up, for the same reason `manageProducts` is: the spec does not
     * say, and this is the tighter reading of a page that carries billing events
     * ("upgraded to Premium, charged £45") and settings changes alongside stock
     * activity. Everyone can already see stock movements on the Activity page —
     * what this adds is who changed the plan, the VAT rate, and someone's role,
     * which is management information rather than shop-floor information.
     *
     * Loosening it later is a one-line change and breaks nothing. Tightening it
     * after staff have been reading the log is the awkward direction.
     */
    viewAuditLog: ROLE_RANK.manager,
};
export function can(role, permission) {
    return ROLE_RANK[role] >= PERMISSIONS[permission];
}
/** For error messages: "you need to be a manager or owner to do that". */
export function requiredRoleFor(permission) {
    const rank = PERMISSIONS[permission];
    return (ROLES.find((r) => ROLE_RANK[r] === rank) ?? 'owner');
}
/**
 * An owner may not be demoted or deactivated while they are the only one, or the
 * account becomes unadministrable — nobody can change billing, and support has to
 * intervene. Checked at the point of change, against the current owner count.
 */
export function isLastOwner(role, activeOwnerCount) {
    return role === 'owner' && activeOwnerCount <= 1;
}
// -------------------------------------------------------- PIN quick-switch
/**
 * A PIN is a convenience on a device someone has ALREADY signed into with a full
 * password — never a credential in its own right. design/PROTOTYPE-SPEC.md §14:
 * "a tap on your name + a per-user 4-digit PIN (POS-style) instead of a full
 * password login, so counter logging stays fast while attribution stays real."
 *
 * Because a 4-digit PIN is only ~10,000 combinations, these limits are the
 * protection, not the PIN itself.
 */
export const PIN_MAX_ATTEMPTS = 5;
/** Long enough to make guessing pointless, short enough not to strand a shift. */
export const PIN_LOCKOUT_MINUTES = 15;
/** PINs are not unique within an account: the person is chosen by tapping a
 *  name, so two people sharing 1234 is harmless. Only sequences and repeats are
 *  refused, because those are what people actually pick. */
export const WEAK_PINS = [
    '0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999',
    '1234', '4321', '0123', '9876', '1122', '1212', '2121',
    '000000', '111111', '123456', '654321', '121212',
];
export function isWeakPin(pin) {
    if (WEAK_PINS.includes(pin))
        return true;
    // All the same digit, at any length.
    if (/^(\d)\1+$/.test(pin))
        return true;
    // Strictly ascending or descending runs, e.g. 3456 or 8765.
    const digits = [...pin].map(Number);
    const ascending = digits.every((d, i) => i === 0 || d === (digits[i - 1] ?? 0) + 1);
    const descending = digits.every((d, i) => i === 0 || d === (digits[i - 1] ?? 0) - 1);
    return ascending || descending;
}
//# sourceMappingURL=roles.js.map